import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { Avatar } from '@/shared/components/ui/Avatar'
import { Badge } from '@/shared/components/ui/Badge'
import { formatIndianDateTime } from '@/shared/utils/dateTime'
import { activityMeta } from './activityMeta'
import type { TimelineActivity } from './types'

function stamp(at: string) {
  const d = new Date(at)
  if (Number.isNaN(d.getTime())) return { date: '', time: '' }
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    weekday: 'short',
  }).formatToParts(d)
  const get = (t: string) => parts.find((p) => p.type === t)?.value || ''
  return {
    date: `${get('day')} ${get('month')} ${get('year')}`,
    time: `${get('weekday')} ${get('hour')}:${get('minute')} ${get('dayPeriod').toLowerCase()}`,
  }
}

interface Props {
  activities: TimelineActivity[]
  loading?: boolean
}

export function ActivityTimeline({ activities, loading }: Props) {
  if (loading && activities.length === 0) {
    return <p className="py-10 text-center text-sm text-muted-foreground">Loading this deal's history…</p>
  }
  if (activities.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border/60 bg-muted/10 py-10 text-center">
        <p className="text-sm font-medium text-foreground">Nothing recorded yet</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Log a call, book an appointment or add a note to start this deal's timeline.
        </p>
      </div>
    )
  }

  return (
    <ol className="relative space-y-3 pl-2">
      {activities.map((a) => {
        const meta = activityMeta(a.type)
        const Icon = meta.icon
        const when = stamp(a.at)
        const body = (
          <div className="flex flex-1 items-start justify-between gap-3 rounded-xl border border-border/60 bg-card px-4 py-3 shadow-2xs transition-colors hover:border-primary/40">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground">{a.title}</p>
              {a.detail && (
                <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{a.detail}</p>
              )}
              {a.upcoming && (
                <Badge tone="warning" size="sm" className="mt-2">
                  Upcoming
                </Badge>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {a.actor ? (
                <div className="flex items-center gap-2">
                  <Avatar name={a.actor.name} size={28} />
                  <div className="hidden sm:block">
                    <p className="max-w-[120px] truncate text-xs font-semibold text-foreground">{a.actor.name}</p>
                    <p className="text-[10px] text-muted-foreground">{a.source === 'system' ? 'System' : 'Collaborator'}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-muted text-muted-foreground">
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-[10px] text-muted-foreground">System</span>
                </div>
              )}
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </div>
          </div>
        )

        return (
          <li key={a.id} className="flex gap-3">
            <div className="w-[86px] shrink-0 pt-1 text-right">
              <p className="text-xs font-semibold text-foreground">{when.date}</p>
              <p className="text-[11px] text-muted-foreground">{when.time}</p>
            </div>
            <span
              className={`mt-1.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full shadow-2xs ${meta.tone}`}
              title={meta.label}
            >
              <Icon className="h-4 w-4" />
            </span>
            {a.link?.kind === 'lead' ? (
              <Link to={`/app/leads?view=${a.link.id}`} className="flex flex-1 min-w-0">
                {body}
              </Link>
            ) : a.link?.kind === 'deal' ? (
              <Link to={`/app/deals?view=${a.link.id}`} className="flex flex-1 min-w-0">
                {body}
              </Link>
            ) : (
              <div className="flex flex-1 min-w-0">{body}</div>
            )}
          </li>
        )
      })}
    </ol>
  )
}
